import { addMonths, format, subMonths } from "date-fns";

/**
 * Pads chart data array with empty months to ensure consistent bar widths
 * @param data - Array of data points with month property
 * @param monthsToShow - Number of months to display (default: 6)
 * @returns Padded array with consistent number of months, with null values for missing data
 */
export function padChartDataWithEmptyMonths<T extends { month: string }>(
  data: T[],
  monthsToShow: number,
): T[] {
  // Always use today's date from the browser as the reference point
  const today = new Date();

  // Create a map for quick lookup
  const dataMap = new Map(data.map((item) => [item.month, item]));

  // Generate array of months going back from today
  const paddedData: T[] = [];

  for (let i = monthsToShow - 1; i >= 0; i--) {
    const monthDate = subMonths(today, i);
    const monthKey = format(monthDate, "yyyy-MM");

    const existingData = dataMap.get(monthKey);
    if (existingData) {
      paddedData.push(existingData);
    } else {
      // Create a placeholder with null values for missing months
      // We'll need to handle this generically for different data types
      paddedData.push({ month: monthKey } as T);
    }
  }

  return paddedData;
}

/**
 * Pads chart data array with future months to ensure consistent bar widths
 * @param data - Array of data points with month property
 * @param monthsToShow - Number of months to display (default: 6)
 * @returns Padded array with consistent number of months, with null values for missing data
 */
export function padChartDataWithFutureMonths<T extends { month: string }>(
  data: T[],
  monthsToShow: number,
): T[] {
  // Always use today's date from the browser as the reference point
  const today = new Date();

  // Create a map for quick lookup
  const dataMap = new Map(data.map((item) => [item.month, item]));

  // Generate array of months going forward from today
  const paddedData: T[] = [];

  for (let i = 0; i < monthsToShow; i++) {
    const monthDate = addMonths(today, i);
    const monthKey = format(monthDate, "yyyy-MM");

    const existingData = dataMap.get(monthKey);
    if (existingData) {
      paddedData.push(existingData);
    } else {
      // Create a placeholder with null values for missing months
      paddedData.push({ month: monthKey } as T);
    }
  }

  return paddedData;
}
